<template>
  <div data-testid="strapi-media-gallery">
    <div :class="inContainer ? 'max-w-screen-xl mx-auto px-4' : ''">
      <h2 v-if="showTitle" class="mb-8 uppercase">
        {{ component.title }}
      </h2>
      <div ref="galleryContainer" class="relative my-8">
        <!-- Fade-stack: all items overlap; active one is relative (sets height) -->
        <div class="relative overflow-hidden rounded-md">
          <div
            v-for="(item, index) in galleryItems"
            :key="item.id"
            class="transition-opacity duration-500 ease-in-out"
            :class="
              currentItemIndex === index
                ? 'relative z-1 opacity-100'
                : 'absolute inset-0 z-0 opacity-0 pointer-events-none'
            "
          >
            <media-gallery-video
              v-if="item.component === 'media-gallery.video'"
              ref="videoRefs"
              :is-active="currentItemIndex === index"
              :video="item"
            />
            <media-gallery-image
              v-if="item.component === 'media-gallery.image'"
              :class="{ 'cursor-pointer': showFullscreenButton }"
              :image="item"
              @click="showFullscreenButton && openModal()"
            />
          </div>
        </div>

        <!-- prev / next controls -->
        <button
          v-if="galleryItems.length > 1"
          aria-label="Previous"
          class="hidden lg:flex size-8 rounded-full items-center justify-center absolute top-1/2 -translate-y-1/2 left-4 ml-4 shadow z-10 bg-charcoal"
          type="button"
          @click="prevItem"
        >
          <fa-icon
            class="size-5 text-rhino"
            :icon="faChevronLeft"
            aria-hidden="true"
          />
        </button>

        <button
          v-if="galleryItems.length > 1"
          aria-label="Next"
          class="hidden lg:flex size-8 rounded-full items-center justify-center absolute top-1/2 -translate-y-1/2 right-4 mr-4 shadow z-10 bg-charcoal"
          type="button"
          @click="nextItem"
        >
          <fa-icon
            class="size-5 text-rhino"
            :icon="faChevronRight"
            aria-hidden="true"
          />
        </button>

        <strapi-media-gallery-modal
          v-if="
            showFullscreenButton &&
            currentItem != null &&
            currentItem.component === 'media-gallery.image'
          "
          ref="modalRef"
          :current-item="currentItem"
        />
      </div>

      <strapi-media-gallery-thumbnails
        :current-item-index="currentItemIndex"
        :items="galleryItems"
        @thumbnail-clicked="setCurrentSlide"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import type {
  StrapiMediaGallery,
  StrapiMediaGalleryItem,
  StrapiProductGallery,
} from '~/apollo/types/types';

defineOptions({
  name: 'StrapiMediaGallery',
});

const props = withDefaults(
  defineProps<{
    component: StrapiMediaGallery | StrapiProductGallery;
    showTitle?: boolean;
    /** When true (e.g. landing), add max-w mx-auto px-4. When false (e.g. news), parent already provides container. */
    inContainer?: boolean;
  }>(),
  {
    showTitle: true,
    inContainer: true,
  },
);

const galleryContainer = ref<HTMLElement>();
const modalRef = ref();
const openModal = () => {
  modalRef.value?.openModal();
};
const currentItemIndex = ref(0);

/**
 * Normalizes both gallery data shapes into a unified items array.
 * - StrapiMediaGallery: already has `items[]`
 * - StrapiProductGallery (legacy): has `images[]` which we wrap as image items
 */
const galleryItems = computed<StrapiMediaGalleryItem[]>(() => {
  if ('items' in props.component) {
    return props.component.items.filter(
      (item): item is NonNullable<typeof item> => item != null,
    );
  }
  // Legacy product.gallery: wrap each image as a media-gallery.image item
  return props.component.images.map((image, index) => ({
    component: 'media-gallery.image',
    id: `legacy-${props.component.id}-${index}`,
    image,
  }));
});

const currentItem = computed(() => galleryItems.value[currentItemIndex.value]);

const videoRefs = ref([]);

const showFullscreenButton = ref(false);
const { width, height } = useWindowSize();
const setShowFullscreenButton = () => {
  if (galleryContainer.value == null) return;

  showFullscreenButton.value =
    galleryContainer.value.clientWidth + 50 < width.value &&
    galleryContainer.value.clientHeight + 50 < height.value;
};

const watchScreenWidthHandler = watchThrottled(
  () => width.value,
  setShowFullscreenButton,
  { throttle: 500 },
);

const watchScreenHeightHandler = watchThrottled(
  () => height.value,
  setShowFullscreenButton,
  { throttle: 500 },
);

const nextItem = () => {
  currentItemIndex.value =
    (currentItemIndex.value + 1) % galleryItems.value.length;
};

const prevItem = () => {
  currentItemIndex.value =
    (currentItemIndex.value - 1 + galleryItems.value.length) %
    galleryItems.value.length;
};

const setCurrentSlide = (index: number) => {
  currentItemIndex.value = index;
};

onMounted(() => {
  setShowFullscreenButton();
});

onUnmounted(() => {
  watchScreenWidthHandler();
  watchScreenHeightHandler();
});
</script>
